import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";

export default function Concerts() {
  const [concerts, setConcerts] = useState([]);
  const [artists, setArtists] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/concerts").then((r) => setConcerts(r.data)).catch(() => {});
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
  }, []);

  const artistName = (id) => artists.find((a) => a.id === id)?.name || "Joy Messenger";
  const filtered = useMemo(
    () => (filter === "all" ? concerts : concerts.filter((c) => c.artist === filter)),
    [concerts, filter]
  );
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = filtered.filter((c) => c.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = filtered.filter((c) => c.date < today).sort((a, b) => b.date.localeCompare(a.date));

  const renderRow = (c, i, isPast = false) => {
    const d = new Date(c.date + "T12:00:00");
    return (
      <Reveal key={c.id} delay={0.04 * i}>
        <div
          className={`group flex flex-wrap items-center gap-5 sm:gap-10 py-7 border-b border-jm-brass/10 ${isPast ? "opacity-60" : ""}`}
          data-testid={`concert-row-${c.id}`}
        >
          <div className="shrink-0 w-20 text-center border-r border-jm-brass/15 pr-5 sm:pr-10">
            <p className="font-serif text-4xl text-jm-bone leading-none">{d.getDate()}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-jm-brass mt-2">
              {d.toLocaleDateString("fr-FR", { month: "short" }).replace(".", "")} {d.getFullYear()}
            </p>
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-jm-amber">{artistName(c.artist)}</p>
            <h2 className="mt-1.5 font-serif text-2xl text-jm-bone group-hover:text-jm-brass transition-colors duration-300">
              {c.venue}
            </h2>
            <p className="text-sm text-jm-mute mt-1">{c.city}</p>
          </div>
          {!isPast &&
            (c.ticket_url ? (
              <a
                href={c.ticket_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`concert-ticket-${c.id}`}
                className="inline-flex items-center gap-2 border border-jm-brass/40 px-6 py-3 text-[13px] text-jm-bone hover:border-jm-amber hover:text-jm-amber transition-colors"
              >
                Réserver <ArrowUpRight size={13} />
              </a>
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute">Billetterie bientôt</span>
            ))}
          {isPast && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute">Concert passé</span>}
        </div>
      </Reveal>
    );
  };

  return (
    <main data-testid="concerts-page" className="pt-28 sm:pt-36">
      <div className="jm-container pb-24 sm:pb-32">
        <Reveal>
          <p className="jm-overline mb-5">Sur scène</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-jm-bone leading-[1.08]">
            Concerts
          </h1>
          <p className="mt-6 max-w-2xl text-jm-smoke leading-relaxed">
            La musique de Joy Messenger vit d'abord sur scène. Retrouvez toutes les dates des quatre projets.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-2.5" data-testid="concert-filters">
            <button
              data-testid="filter-all"
              onClick={() => setFilter("all")}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border transition-colors duration-300 ${
                filter === "all" ? "border-jm-amber bg-jm-amber text-jm-ink" : "border-jm-brass/25 text-jm-smoke hover:text-jm-bone"
              }`}
            >
              Tous
            </button>
            {artists.map((a) => (
              <button
                key={a.id}
                data-testid={`filter-${a.id}`}
                onClick={() => setFilter(a.id)}
                className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border transition-colors duration-300 ${
                  filter === a.id ? "border-jm-amber bg-jm-amber text-jm-ink" : "border-jm-brass/25 text-jm-smoke hover:text-jm-bone"
                }`}
              >
                {a.name}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 border-t border-jm-brass/10" data-testid="concerts-list">
          {filtered.length === 0 && (
            <p className="py-12 text-jm-mute text-sm">Aucune date pour ce projet pour le moment.</p>
          )}
          {[...upcoming, ...past].map((c, i) => renderRow(c, i, c.date < today))}
        </div>
      </div>
    </main>
  );
}
